/* eslint-disable array-callback-return */
import { action, observable, makeObservable, computed } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';

import { LocationAddressResponse } from 'ui/types';

import Logger from 'utils/logger';
import { EventType } from 'utils/events/constants';

import { EntityModules } from 'core/constants';
import { TaskStatus } from 'utils/api/modules/tasks/tasks.constants';
import { LinkedTaskType, TaskListResponse } from 'utils/api/modules/tasks/tasks.types';

import RootStore from 'stores/Root';
import API from 'utils/api';

import {
  FormsDetailsResponse,
  FormStatus,
  isMultipleAnswerQuestionType,
  TemplateNameContent,
  SelectedAnswerType,
  InspectionEntity,
} from 'utils/api/modules/workflows/workflows.types';

import { LinksType, LocationProperties } from 'utils/api/types';
import { UserNameResponse } from 'utils/api/modules/user/user.types';

import { ActionEvent } from 'utils/events/types';

import { MultipleAnswerProperties, QuestionType, SectionType } from 'stores/TemplateDetails/types';

import { ScoreChipColors } from 'containers/FormsDetails/types';

import { isBadRequest } from 'utils/api/errors';

import { getEntityTranslation } from 'stores/EntityDetails/utils';

import AnswerDetails from './AnswerDetails';

type Answers = { [key: string]: AnswerDetails };
type ScoreValues = { scoredPoints: string; allPoints: string; average: string; bgColor?: string };

export const EntitiesForAuditsForms = [
  EntityModules.Sites,
  EntityModules.Assets,
  EntityModules.Contacts,
  EntityModules.Documents,
];

export default class DetailsForm {
  rootStore: RootStore;

  api: typeof API;

  @observable uuid: string;

  @observable name: string;

  @observable answers: Answers[];

  @observable template: TemplateNameContent;

  @observable location: LocationProperties | null;

  @observable reporter: UserNameResponse;

  @observable submitted: string | null;

  @observable linkedTasks: LinkedTaskType;

  @observable links: Array<LinksType>;

  @observable scores: ScoreValues | null;

  @observable submitting = false;

  @observable updating = false;

  @computed get questions(): { [key: string]: QuestionType } {
    if (!Object.keys(this.sections).length) {
      return {};
    }
    const sectionsArray = Object.values(this.sections);
    const questionsArray = sectionsArray.flatMap((section) => Object.entries(section.properties));
    return Object.fromEntries(questionsArray);
  }

  constructor(rootStore: RootStore, api: typeof API, inspectionId: string, data: FormsDetailsResponse) {
    makeObservable(this);
    this.rootStore = rootStore;
    this.api = api;
    this.uuid = data.uuid;
    this.name = data.name;
    this.template = data.template;
    this.answers = this.getAnswers(inspectionId, data);
    this.reporter = data.reporter;
    this.submitted = data.submitted;
    this.linkedTasks = data.linkedTasks;
    this.location = data.location;
    this.scores = this.getAllScore();
    this.links = data.links;

    eventBus.register(this);
  }

  @action.bound
  async updateForm(
    uuid: string,
    name: string,
    location: LocationAddressResponse,
    ...entities: (string | null)[]
  ): Promise<boolean> {
    try {
      const updatedData: { [key: string]: string | LocationAddressResponse | null } = { name, location };
      EntitiesForAuditsForms.forEach((entity, index) => {
        updatedData[getEntityTranslation[entity].singularKey] = entities[index] || null;
      });

      const { data } = await this.api.workflows.updateForm(uuid, updatedData)();
      this.rootStore.notification.enqueueSuccessSnackbar('Inspection updated successfully');
      this.name = data.name;
      this.location = data.location;
      this.links = data.links;
      return true;
    } catch (e) {
      this.rootStore.notification.enqueueErrorSnackbar('Could not update user details');
      // @ts-ignore
      if (!isBadRequest(e)) {
        Logger.error('Invalid update user details API response', e);
      }
      return false;
    }
  }

  getAnswers(inspectionId: string, data: FormsDetailsResponse): Answers[] {
    const answers: Answers[] = [];

    this.template.content.order.map((sectionId) => {
      answers.push(
        Object.fromEntries(
          Object.keys(this.template.content.properties[sectionId].properties).map((questionId) => [
            questionId,
            new AnswerDetails(
              this.rootStore,
              this.api,
              inspectionId,
              questionId,
              data.details[questionId],
              data.stats[questionId]
            ),
          ])
        )
      );
    });

    return answers;
  }

  bgColor(average: number): string {
    if (average <= 49) {
      return ScoreChipColors.orange;
    }
    if (average > 50 && average <= 69) {
      return ScoreChipColors.yellow;
    }
    if (average >= 70 && average <= 89) {
      return ScoreChipColors.blue;
    }
    if (average >= 90) {
      return ScoreChipColors.green;
    }
    return ScoreChipColors.green;
  }

  @action
  getAllScore(): ScoreValues | null {
    if (this.submitted === null) {
      return null;
    }
    const allScoreResult: ScoreValues = { scoredPoints: '', allPoints: '', average: '' };

    this.template.content.order.map((sectionId) => {
      const { allPoints, scoredPoints } = this.getSectionScore(this.template.content.properties[sectionId], sectionId);
      if (!isNaN(Number(scoredPoints)) && !isNaN(Number(allPoints))) {
        allScoreResult.scoredPoints = String(Number(allScoreResult.scoredPoints) + Number(scoredPoints));
        allScoreResult.allPoints = String(Number(allScoreResult.allPoints) + Number(allPoints));
      }
    });

    const average = Math.round((Number(allScoreResult.scoredPoints) / Number(allScoreResult.allPoints)) * 100);

    return {
      scoredPoints: String(allScoreResult.scoredPoints),
      allPoints: String(allScoreResult.allPoints),
      average: `${average}%`,
      bgColor: this.bgColor(average),
    };
  }

  @action
  getSectionScore(section: SectionType, sectionId: string): ScoreValues {
    const sectionScoreResult: ScoreValues = { scoredPoints: '', allPoints: '', average: '', bgColor: '' };

    section.order.forEach((o) => {
      const questionProperties = section.properties[o] as QuestionType;
      const sectionID = this.template.content.order.indexOf(sectionId) as number;

      const questionAnswers = this.answers[sectionID][o];

      const score =
        questionProperties.scoring && isMultipleAnswerQuestionType(questionProperties) && this.submitted
          ? (this.getScore(questionProperties, questionAnswers && (questionAnswers.answer as string[])) as ScoreValues)
          : null;

      if (score) {
        sectionScoreResult.scoredPoints = String(Number(sectionScoreResult.scoredPoints) + Number(score?.scoredPoints));
        sectionScoreResult.allPoints = String(Number(sectionScoreResult.allPoints) + Number(score?.allPoints));
      }
    });

    sectionScoreResult.average = `${Math.round(
      (Number(sectionScoreResult.scoredPoints) / Number(sectionScoreResult.allPoints)) * 100
    )}%`;
    sectionScoreResult.bgColor = this.bgColor(
      Math.round((Number(sectionScoreResult.scoredPoints) / Number(sectionScoreResult.allPoints)) * 100)
    );

    return sectionScoreResult;
  }

  @action
  getScore(properties: MultipleAnswerProperties, answers: string[]): ScoreValues {
    const {
      items: { enum: propEnum },
      scoring,
      maxItems,
    } = properties;
    let sumOfPoints = 0;
    let score = 1;

    if (!answers) {
      return {
        scoredPoints: '0',
        allPoints: '0',
        average: '0%',
      };
    }
    answers.forEach((answer) => {
      if (propEnum.includes(answer) && scoring) {
        const index = properties.items.enum.indexOf(answer);
        sumOfPoints += Number(scoring[index]);
      }
    });

    if (maxItems && scoring) {
      score = Math.max(...scoring.map(Number));
    } else {
      score = scoring?.map(Number)?.reduce((sum, current) => sum + current) || 0;
    }

    return {
      scoredPoints: sumOfPoints as unknown as string,
      allPoints: score as unknown as string,
      average: `${Math.round((sumOfPoints / score) * 100)}%`,
    };
  }

  @subscribe(EventType.CreatedTask)
  @subscribe(EventType.CreatedTaskAnswerTask)
  @action
  async createdTask({ payload }: ActionEvent<TaskListResponse>): Promise<void> {
    if (payload.links.some((link) => link.entity === InspectionEntity && link.instance.uuid === this.uuid)) {
      this.linkedTasks = {
        pending: this.linkedTasks.pending + (payload.status !== TaskStatus.Completed ? 1 : 0),
        completed: this.linkedTasks.completed + (payload.status === TaskStatus.Completed ? 1 : 0),
        total: this.linkedTasks.total + 1,
      };
    }
  }

  @subscribe(EventType.UpdatedTask)
  updatedTask({ payload }: ActionEvent<TaskListResponse>): void {
    if (payload.status === TaskStatus.Completed) {
      this.linkedTasks = {
        ...this.linkedTasks,
        pending: this.linkedTasks.pending - 1,
        completed: this.linkedTasks.completed + 1,
      };
    }
  }

  updatedInspection({ payload }: ActionEvent<FormsDetailsResponse>): void {
    if (payload.uuid !== this.uuid) {
      return;
    }
    this.name = payload.name;
    this.template = payload.template;
    this.reporter = payload.reporter;
    this.submitted = payload.submitted;
    this.linkedTasks = payload.linkedTasks;
    this.location = payload.location;
  }

  get order(): string[] {
    if (!this.template.content) {
      return [];
    }

    return this.template.content.order;
  }

  get sections(): { [key: string]: SectionType } {
    if (!this.template.content) {
      return {};
    }

    return this.template.content.properties;
  }

  setAnswer(answerKey: string, selectedAnswer: SelectedAnswerType): void {
    const sectionId = answerKey.split('::')[0];
    const sectionIndex = this.order.indexOf(sectionId);

    this.answers[sectionIndex][answerKey].setAnswer(selectedAnswer);
  }

  @action.bound
  async updateAnswer(answerKey: string, selectedAnswer: SelectedAnswerType): Promise<void> {
    this.updating = true;
    try {
      this.setAnswer(answerKey, selectedAnswer);
      const result = {};

      this.answers.forEach((obj) => {
        Object.keys(obj).forEach((key) => {
          if (obj[key]) {
            // @ts-ignore
            result[key] = obj[key].answer;
          }
        });
      });

      await this.api.workflows.updateForm(this.uuid, {
        details: result,
      })();
    } catch (e) {
      this.rootStore.notification.enqueueErrorSnackbar('Could not update your answer');
      Logger.error(
        `Invalid update inspection details API response. Inspection id: ${this.uuid} AnswerId id: ${answerKey}`,
        e
      );
    } finally {
      // add some delay to show the loading spinner
      setTimeout(() => {
        this.updating = false;
      }, 1500);
    }
  }

  getUnansweredRequiredQuestions(): string[] {
    const allMandatoryQuestions = this.template.content.order.flatMap(
      (sectionId) => this.template.content.properties[sectionId].mandatory
    );

    return allMandatoryQuestions.filter((questionId) => {
      const sID = questionId.split('::')[0];
      const sectionIndex = this.order.indexOf(sID);
      const { answer } = this.answers[sectionIndex][questionId];
      return answer === undefined || answer === null || answer === '';
    });
  }

  getUnansweredRequiredQuestionsCount(): number {
    return this.getUnansweredRequiredQuestions().length;
  }

  @action.bound
  async submitForm(): Promise<void> {
    this.submitting = true;
    try {
      const unansweredCount = this.getUnansweredRequiredQuestionsCount();
      if (unansweredCount > 0) {
        this.rootStore.notification.enqueueErrorSnackbar(`${unansweredCount} mandatory questions incomplete `);

        this.submitting = false;
        return;
      }
      const { data } = await this.api.workflows.updateForm(this.uuid, { status: FormStatus.Submitted })();

      this.submitted = data.submitted;
      this.scores = this.getAllScore();
      this.rootStore.notification.enqueueSuccessSnackbar('Form submitted successfully');
    } catch (e) {
      this.rootStore.notification.enqueueErrorSnackbar('Could not submit your form');
    }
    this.submitting = false;
  }
}
